export const SUPPORTED_LOCALES = [
  'en',
  'ar',
  'es',
  'pt-BR',
  'fr',
  'it',
  'ko',
  'de',
  'ja',
  'hi',
  'ru',
  'zh',
  'zh-TW',
  'vi',
  'id',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';
export const LOCALE_COOKIE_NAME = 'imai-locale';
export const AUTH_LOCALE_INTENT_COOKIE_NAME = 'imai-auth-locale-intent';
export const LOCALE_COOKIE_MAX_AGE_SECONDS = 31_536_000;
export const AUTH_LOCALE_INTENT_COOKIE_MAX_AGE_SECONDS = 86_400;

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  'pt-BR': 'Portuguese (Brazil)',
  ar: 'Arabic',
  es: 'Spanish',
  fr: 'French',
  it: 'Italian',
  ko: 'Korean',
  de: 'German',
  ja: 'Japanese',
  hi: 'Hindi',
  ru: 'Russian',
  zh: 'Chinese',
  'zh-TW': 'Traditional Chinese',
  vi: 'Vietnamese',
  id: 'Indonesian',
};

export const LOCALE_NATIVE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  'pt-BR': 'Português (Brasil)',
  ar: 'العربية',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  ko: '한국어',
  de: 'Deutsch',
  ja: '日本語',
  hi: 'हिन्दी',
  ru: 'Русский',
  zh: '中文',
  'zh-TW': '繁體中文',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
};

export function formatLocaleOptionLabel(locale: SupportedLocale): string {
  const nativeLabel = LOCALE_NATIVE_LABELS[locale];
  const englishLabel = LOCALE_LABELS[locale];
  return nativeLabel === englishLabel
    ? englishLabel
    : `${nativeLabel} (${englishLabel})`;
}

export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function normalizeLocale(
  value: string | null | undefined
): SupportedLocale {
  if (!value) {
    return DEFAULT_LOCALE;
  }

  if (isSupportedLocale(value)) {
    return value;
  }

  const normalized = value.toLowerCase();

  if (
    normalized === 'pt-br' ||
    normalized === 'pt_br' ||
    normalized.startsWith('pt-br') ||
    normalized === 'brazilian portuguese' ||
    normalized === 'português brasileiro' ||
    normalized === 'português (brasil)'
  ) {
    return 'pt-BR';
  }

  if (normalized === 'de-at' || normalized.startsWith('de-at')) {
    return 'de';
  }
  if (
    normalized === 'zh-tw' ||
    normalized === 'zh_hant' ||
    normalized === 'zh-hant' ||
    normalized === 'zh-hant-tw'
  ) {
    return 'zh-TW';
  }
  if (normalized.startsWith('zh')) {
    return 'zh';
  }
  if (normalized.startsWith('vi')) {
    return 'vi';
  }
  if (normalized === 'id' || normalized.startsWith('id-')) {
    return 'id';
  }
  if (normalized.startsWith('de')) {
    return 'de';
  }
  if (normalized.startsWith('ar')) {
    return 'ar';
  }
  if (normalized.startsWith('es')) {
    return 'es';
  }
  if (normalized.startsWith('fr')) {
    return 'fr';
  }
  if (normalized.startsWith('it')) {
    return 'it';
  }
  if (normalized.startsWith('ko')) {
    return 'ko';
  }
  if (normalized.startsWith('ja')) {
    return 'ja';
  }
  if (normalized.startsWith('hi')) {
    return 'hi';
  }
  if (normalized.startsWith('ru')) {
    return 'ru';
  }

  return DEFAULT_LOCALE;
}

export function getDocumentDirection(
  locale: string | null | undefined
): 'ltr' | 'rtl' {
  return normalizeLocale(locale) === 'ar' ? 'rtl' : 'ltr';
}

const LOCALE_FALLBACKS: Partial<Record<SupportedLocale, SupportedLocale>> = {
  'zh-TW': 'zh',
  'pt-BR': 'en',
  vi: 'en',
  id: 'en',
};

export function resolveAvailableLocale<T extends string>(
  locale: string | null | undefined,
  availableLocales: readonly T[],
  defaultLocale: T
): T {
  const normalized = normalizeLocale(locale);
  if (availableLocales.includes(normalized as T)) {
    return normalized as T;
  }

  const fallback = LOCALE_FALLBACKS[normalized];
  if (fallback && availableLocales.includes(fallback as T)) {
    return fallback as T;
  }

  return availableLocales.includes(defaultLocale)
    ? defaultLocale
    : (availableLocales[0] ?? defaultLocale);
}
